import { updateSelectedVolumeForEntry } from "#redux/actions/recordOfVolume";
import {
  CUBIC_METERS_VOLUME,
  getFormattedMeasurementValueWithUnit,
} from "#redux/reducers/measurements";
import {
  Button,
  LegacyDataTable,
  Tooltip,
} from "@validereinc/common-components";
import { find, union } from "es-toolkit/compat";
import * as PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { AutoSizer } from "react-virtualized";
import AdjustmentFactorsCell from "../AdjustmentFactorCell";
import { getRecordCheckboxState } from "../RecordOfVolumeHelper";
import "./DailyVolumeTable.scss";
import useFilterInputs from "./DailyVolumeTableFilter";
import {
  getInventoryVolumeByDays,
  sortByNotes,
  sortByVolume,
} from "./DailyVolumeTableHelper";

const SOURCE_SHORT_LABELS = {
  production_management_system: "PMS",
};

const mapStateToProps = (state) => {
  return {
    selectedRecordOfVolumes: state.recordOfVolume?.toJS() ?? [],
    streams: state.streams?.data.toJS() ?? [],
    getFormattedMeasurementValueWithUnit: (measurementKey, measurementObject) =>
      getFormattedMeasurementValueWithUnit(state.measurements)(
        measurementKey,
        measurementObject
      ),
  };
};

const mapDispatchToProps = {
  updateSelectedVolumeForEntry,
};

const InventoryDailyVolumeTable = (props) => {
  const { volumes, dateRange, adjustmentFactors, streamId } = props;

  const [dailyVolumes, setDailyVolumes] = useState<any[]>([]);
  const [checkedDailyVolume, setCheckedDailyVolume] = useState<any[]>([]);
  const [filterRow, filterPillbox, filteredDailyVolumes] = useFilterInputs(
    dailyVolumes,
    adjustmentFactors
  );

  useEffect(() => {
    if (dateRange && volumes && props.streams) {
      const { from, to } = dateRange;

      const dailyVolumes = getInventoryVolumeByDays(
        from,
        to,
        volumes,
        props.streams,
        streamId
      );

      setDailyVolumes(dailyVolumes);
    }
  }, [dateRange, volumes, props.streams]);

  const onActionClick = (selectedStream) => {
    const { is_entry_required, is_locked } = selectedStream;

    props.updateSelectedVolumeForEntry(
      union([selectedStream], checkedDailyVolume)
    );

    if (!props.haveWritePermission || is_locked) {
      props.setModalState("view");
    }

    if (is_entry_required && !is_locked) {
      props.setModalState("add");
    }

    if (!is_entry_required && !is_locked) {
      props.setModalState("edit");
    }
  };

  const onBatchEditButtonClick = () => {
    props.updateSelectedVolumeForEntry(checkedDailyVolume);
    props.setModalState("add");
  };

  const onStreamCheckboxClick = (checkedStreams) => {
    setCheckedDailyVolume(checkedStreams);
  };

  const stateRenderer = (rowData) => {
    return rowData.is_locked ? "Locked" : "Unlocked";
  };

  const streamRenderer = (rowData) => {
    const streamName = rowData?.streamName ?? "-";

    return <Tooltip content={streamName}>{streamName}</Tooltip>;
  };

  const sourceRenderer = (rowData) => {
    if (rowData.source) {
      const sourceShortForm = SOURCE_SHORT_LABELS[rowData.source];
      return sourceShortForm ?? rowData.source;
    } else {
      return "-";
    }
  };

  const volumeRenderer = (rowData, columnKey) => {
    const totalVolume = rowData[columnKey];

    if (totalVolume) {
      return (
        <span>
          {props.getFormattedMeasurementValueWithUnit(
            CUBIC_METERS_VOLUME,
            totalVolume
          )}
        </span>
      );
    } else {
      return "-";
    }
  };

  const adjustmentFactorRenderer = (volumeRowData) => {
    const adjustmentFactorsForVolume =
      volumeRowData.adjustment_factor_ids?.map((factorId) => {
        return find(props.adjustmentFactors, { id: factorId });
      }) ?? [];

    return (
      <AdjustmentFactorsCell adjustmentFactors={adjustmentFactorsForVolume} />
    );
  };

  const Headers = [
    {
      label: "Date",
      key: "date",
      width: 100,
      fixed: true,
    },
    {
      label: "Status",
      key: "state",
      width: 150,
      cellRenderer: stateRenderer,
      disableSort: true,
    },
    {
      label: "Stream",
      key: "streamName",
      width: 250,
      cellRenderer: streamRenderer,
    },
    {
      label: "Product",
      key: "product",
      width: 150,
    },
    {
      label: "Source",
      key: "source",
      width: 100,
      cellRenderer: sourceRenderer,
    },
    {
      label: "Opening Volume",
      key: "opening_volume",
      width: 200,
      cellRenderer: volumeRenderer,
      sort: sortByVolume,
      rightAlign: true,
    },
    {
      label: "Closing Volume",
      key: "closing_volume",
      width: 200,
      cellRenderer: volumeRenderer,
      sort: sortByVolume,
      rightAlign: true,
    },
    {
      label: "Adjustment Factors",
      key: "adjustments",
      width: 250,
      cellRenderer: adjustmentFactorRenderer,
      disableSort: true,
    },
    {
      label: "Notes",
      key: "note_count",
      width: 100,
      sort: sortByNotes,
    },
  ];

  const haveCheckedDailyVolume = checkedDailyVolume?.length > 0;

  const BatchEditButton = () => (
    <Button
      // @ts-expect-error legacy opshub typing
      type="primary"
      onClick={onBatchEditButtonClick}
      disabled={!haveCheckedDailyVolume}
    >
      {`Group Edit ${haveCheckedDailyVolume ? checkedDailyVolume.length : ""}`}{" "}
      Volume Entries
    </Button>
  );

  return (
    <div
      className="dailyVolumeTable"
      style={{ height: props.height }}
    >
      <AutoSizer>
        {({ height, width }) => (
          <LegacyDataTable
            width={width}
            height={height}
            headers={Headers}
            list={filteredDailyVolumes}
            onCheckboxClick={
              props.haveWritePermission ? onStreamCheckboxClick : undefined
            }
            filterRow={filterRow}
            filterPillbox={filterPillbox}
            rowHeight={40}
            defaultSortBy="date"
            defaultSortDirection="asc"
            onCellClick={onActionClick}
            highlightRow={true}
            actionRow={<BatchEditButton />}
            getCheckboxDisabledState={getRecordCheckboxState}
          />
        )}
      </AutoSizer>
    </div>
  );
};

InventoryDailyVolumeTable.propTypes = {
  haveWritePermission: PropTypes.bool.isRequired,
  height: PropTypes.number.isRequired,
  volumes: PropTypes.array.isRequired,
  dateRange: PropTypes.object.isRequired,
  adjustmentFactors: PropTypes.array.isRequired,
  updateSelectedVolumeForEntry: PropTypes.func.isRequired,
  setModalState: PropTypes.func,
  streams: PropTypes.array.isRequired,
  streamId: PropTypes.string,
  isInventoryStream: PropTypes.bool,
  getFormattedMeasurementValueWithUnit: PropTypes.func,
};

const InventoryDailyVolumeTableContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(InventoryDailyVolumeTable);

export default InventoryDailyVolumeTableContainer;
